/**
 * <card-carousel> — image carousel inside a product card.
 *
 * The track is a native scroll-snap container, so swiping on touch devices
 * needs no JS. This element wires up the arrows and dots, keeps them in sync
 * with the scroll position, and hides the arrow at either end.
 */
if (!customElements.get('card-carousel')) {
  customElements.define(
    'card-carousel',
    class CardCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-carousel-track]');
        this.prevButton = this.querySelector('[data-carousel-prev]');
        this.nextButton = this.querySelector('[data-carousel-next]');
        this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
        this.count = this.dots.length;
        this.current = 0;

        if (!this.track || this.count < 2) return;

        this.onScroll = this.onScroll.bind(this);
        this.onPrev = this.onPrev.bind(this);
        this.onNext = this.onNext.bind(this);
        this.onDotClick = this.onDotClick.bind(this);

        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.prevButton.addEventListener('click', this.onPrev);
        this.nextButton.addEventListener('click', this.onNext);
        this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));

        this.update();
      }

      disconnectedCallback() {
        if (!this.track) return;
        this.track.removeEventListener('scroll', this.onScroll);
        this.prevButton.removeEventListener('click', this.onPrev);
        this.nextButton.removeEventListener('click', this.onNext);
        this.dots.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
      }

      get slideWidth() {
        return this.track.clientWidth || 1;
      }

      onScroll() {
        this.update();
      }

      onPrev(event) {
        this.stop(event);
        this.goTo(this.current - 1);
      }

      onNext(event) {
        this.stop(event);
        this.goTo(this.current + 1);
      }

      onDotClick(event) {
        this.stop(event);
        this.goTo(Number(event.currentTarget.dataset.carouselDot));
      }

      // The card is wrapped in click handlers/links; controls must not trigger them.
      stop(event) {
        event.preventDefault();
        event.stopPropagation();
      }

      goTo(index) {
        const target = Math.max(0, Math.min(this.count - 1, index));
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollTo({ left: target * this.slideWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
      }

      update() {
        const index = Math.round(this.track.scrollLeft / this.slideWidth);
        this.current = Math.max(0, Math.min(this.count - 1, index));

        this.dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === this.current);
          if (i === this.current) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });

        this.prevButton.toggleAttribute('disabled', this.current === 0);
        this.nextButton.toggleAttribute('disabled', this.current === this.count - 1);
      }
    }
  );
}
